import { Component } from "@theme/component";
import { ThemeEvents } from "@theme/events";

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartCount extends Component {
  requiredRefs = ["cartBubbleCount"];

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? "0", 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : "99+";
  }

  get inDrawer() {
    return this.dataset.context === "drawer";
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
  }

  /**
   * Handles the cart update event.
   *
   * The badge counts the number of distinct line items in the cart (not the
   * total quantity), so we read the authoritative count from `/cart.js` rather
   * than relying on the quantity-based `itemCount` carried by the event. The
   * incremental/absolute `itemCount` is only used as a fallback if the fetch
   * fails.
   * @param {Event} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    const lineItemCount = await this.#fetchLineItemCount();

    if (lineItemCount !== null) {
      this.renderCartBubble(lineItemCount);
      return;
    }

    // Fallback: use the event payload if the cart request failed.
    const itemCount = event.detail.data?.itemCount ?? 0;
    const isIncremental = event.detail.data?.isIncremental ?? false;

    if (isIncremental) {
      let currentCount = parseInt(this.refs.cartBubbleCount.textContent ?? "0", 10);

      if (!this.inDrawer) {
        currentCount += itemCount;
      }

      this.renderCartBubble(currentCount);
    } else {
      this.renderCartBubble(itemCount);
    }
  };

  /**
   * Fetches the number of distinct line items currently in the cart.
   * @returns {Promise<number | null>} The line item count, or null on failure.
   */
  async #fetchLineItemCount() {
    try {
      const root = window.Shopify?.routes?.root ?? "/";
      const response = await fetch(`${root}cart.js`, {
        headers: { Accept: "application/json" },
      });

      if (!response.ok) return null;

      const cart = await response.json();
      return Array.isArray(cart.items) ? cart.items.length : null;
    } catch {
      return null;
    }
  }

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The number of items in the cart.
   */
  renderCartBubble = async (itemCount) => {
    this.currentCartCount = itemCount;

    this.classList.toggle("hidden", itemCount === 0);
    this.classList.toggle("cart-count--small-medium", itemCount > 99);
    document.body.classList.toggle("cart-has-items", itemCount > 0);
  };
}

if (!customElements.get("cart-count")) {
  customElements.define("cart-count", CartCount);
}
